import { Static, TSchema, Type } from '@sinclair/typebox';

import { Nullable } from './generics';

export const SearchSchema = Type.Object({
  search_by: Type.Optional(Type.String()),
  search_query: Type.Optional(Type.String()),
});
export type Search = Static<typeof SearchSchema>;

export const OrderByDirectionSchema = Type.Union([
  Type.Literal('asc'),
  Type.Literal('desc'),
]);
export const OrderBySchema = Type.Object({
  order_by: Type.Optional(Type.String()),
  direction: Type.Optional(OrderByDirectionSchema),
});
export type OrderBy = Static<typeof OrderBySchema>;

export const PaginationSchema = Type.Object({
  size: Type.Number({ minimum: 1 }),
  page: Type.Number({ minimum: 1 }),
});
export type Pagination = Static<typeof PaginationSchema>;

export const BasePaginationQueryParams = Type.Object({
  page: Type.Optional(Type.Number({ minimum: 1 })),
  limit: Type.Optional(Type.Number({ minimum: 1, maximum: 100 })),
});

export const PaginationInfoSchema = Type.Object({
  rows: Type.Number({ minimum: 1 }),
  pages: Type.Number({ minimum: 1 }),
});
export type PaginationInfo = Static<typeof PaginationInfoSchema>;

export const BasePaginationResponse = <T extends TSchema>(schema: T) =>
  Type.Object({
    data: Type.Array(schema),
    pagination: Type.Object({
      page: Type.Number(),
      limit: Type.Number(),
      total: Type.Number(),
      total_pages: Type.Number(),
    }),
  });

export type PaginatedResponse<T> = {
  data: T;
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
};

export const ReadManySchema = Type.Intersect([
  SearchSchema,
  OrderBySchema,
  PaginationSchema,
]);
export type ReadMany = Search & OrderBy & Pagination;

export const WarehouseIdSchema = Type.Object({
  warehouse: Type.String({ format: 'uuid', minLength: 36 }), // UUID length are 32 char + 4 dash
});
export type WarehouseId = Static<typeof WarehouseIdSchema>;

export const OptionalWarehouseIdSchema = Type.Object({
  warehouse: Type.Optional(Type.String({ format: 'uuid', minLength: 36 })), // UUID length are 32 char + 4 dash
});
export type OptionalWarehouseId = Static<typeof OptionalWarehouseIdSchema>;

export const WarehouseIdCategorySchema = Type.Object({
  warehouse: Type.Optional(Type.String({ format: 'uuid', minLength: 36 })),
});
export type WarehouseIdCategory = Static<typeof WarehouseIdCategorySchema>;

export const ProductDivisionQuery = Type.Object({
  divisi_code: Type.Optional(
    Type.KeyOf(
      Type.Object({
        diamond: Type.String(),
        trading: Type.String(),
        fnb: Type.String(),
        equipment: Type.String(),
      })
    )
  ),
});
export type ProductDivision = Static<typeof ProductDivisionQuery>;

// TODO(?): Samain query param divisi di buyer dan admin!
export const BackofficeOrderDivisionQuery = Type.Object({
  division: Type.Optional(
    Type.KeyOf(
      Type.Object({
        diamond: Type.String(),
        trading: Type.String(),
        fnb: Type.String(),
        equipment: Type.String(),
        food: Type.String(),
      })
    )
  ),
});
export type BackofficeOrderDivision = Static<
  typeof BackofficeOrderDivisionQuery
>;

export const BuyerOrderDivisionQuery = Type.Object({
  divisi_code: Type.Optional(
    Type.KeyOf(
      Type.Object({
        diamond: Type.String(),
        trading: Type.String(),
        fnb: Type.String(),
        equipment: Type.String(),
        food: Type.String(),
      })
    )
  ),
});
export type BuyerOrderDivision = Static<typeof BuyerOrderDivisionQuery>;

export const StatusOrderSchema = Type.Object({
  status: Type.Optional(
    Type.KeyOf(
      Type.Object({
        draft: Type.String(),
        submitted: Type.String(),
        waiting_for_payment: Type.String(),
        processed: Type.String(),
        partially_processed: Type.String(),
        out_for_delivery: Type.String(),
        partially_out_for_delivery: Type.String(),
        received: Type.String(),
        partially_received: Type.String(),
        cancelled: Type.String(),
      })
    )
  ),
});
export type StatusOrder = Static<typeof StatusOrderSchema>;

export const SortBySchema = Type.Object({
  sort_by_code: Type.Optional(
    Type.KeyOf(
      Type.Object({
        best_seller_desc: Type.String(),
        brand_asc: Type.String(),
        brand_desc: Type.String(),
        product_name_asc: Type.String(),
        product_name_desc: Type.String(),
        price_asc: Type.String(),
        price_desc: Type.String(),
      })
    )
  ),
});
export type SortBy = Static<typeof SortBySchema>;

export const DateFilterSchema = Type.Object({
  start_date: Type.Optional(Type.Integer({ minimum: 0 })),
  end_date: Type.Optional(Type.Integer({ minimum: 0 })),
});
export type DateFilter = Static<typeof DateFilterSchema>;

export const ReadManyProductSchema = Type.Intersect([
  Type.Omit(SearchSchema, ['search_by']),
  Type.Object({
    size: Type.Number({ minimum: 1, maximum: 100 }),
    page: Type.Number({ minimum: 1 }),
  }),
  ProductDivisionQuery,
  SortBySchema,
  Type.Object({
    category_lv1: Type.Optional(Type.String()),
    category_lv2: Type.Optional(Type.String()),
    category_lv3: Type.Optional(Type.String()),
    storage_code: Type.Optional(
      Type.KeyOf(
        Type.Object({
          dry: Type.String(),
          frozen: Type.String(),
          dry_ac: Type.String(),
          chilled: Type.String(),
        })
      )
    ),
    brand_name: Type.Optional(Type.String()),
  }),
  Type.Object({
    warehouse: Type.Optional(Type.String({ format: 'uuid', minLength: 36 })), // UUID length are 32 char + 4 dash
    is_public: Type.Optional(Type.Boolean()),
  }),
]);
export type ReadManyProduct = Static<typeof ReadManyProductSchema>;

export const readActivitiesSchema = Type.Intersect([
  ReadManySchema,
  StatusOrderSchema,
  DateFilterSchema,
  BackofficeOrderDivisionQuery,
  Type.Object({
    account_name: Type.Optional(Nullable(Type.String())),
    distribution_channel: Type.Optional(Nullable(Type.String())),
    do_automation: Type.Optional(
      Type.Union([
        Type.Literal('all'),
        Type.Literal('true'),
        Type.Literal('false'),
      ])
    ),
    category_2: Type.Optional(
      Type.Union([Type.Literal('commodity'), Type.Literal('non_commodity')])
    ),
    cg_1_business_segmentation: Type.Optional(
      Nullable(Type.Array(Type.String()))
    ),
  }),
]);
export type readActivities = Static<typeof readActivitiesSchema>;

export const ReadActivityDetailSchema = Type.Intersect([
  OrderBySchema,
  PaginationSchema,
]);

export const ReadManyOrderTemplateSchema = Type.Intersect([
  PaginationSchema,
  Type.Object({
    warehouse: Type.Optional(Type.String({ format: 'uuid' })), // later change to required
    search_query: Type.Optional(Type.String()),
  }),
]);
export type ReadManyOrderTemplate = Static<typeof ReadManyOrderTemplateSchema>;

export const ReadWishlistSchema = Type.Intersect([
  PaginationSchema,
  ProductDivisionQuery,
  SortBySchema,
  Type.Object({
    warehouse: Type.Optional(Type.String({ format: 'uuid' })), // later change to required
  }),
]);
export type ReadWishlist = Static<typeof ReadWishlistSchema>;

export const ReadManyOrderTemplateItemSchema = Type.Intersect([
  PaginationSchema,
  ProductDivisionQuery,
]);
export type ReadManyOrderTemplateItem = Static<
  typeof ReadManyOrderTemplateItemSchema
>;

export const ReadManyOrderHistorySchema = Type.Intersect([
  PaginationSchema,
  StatusOrderSchema,
  BuyerOrderDivisionQuery,
  DateFilterSchema,
  WarehouseIdSchema,
  Type.Object({
    search: Type.Optional(Type.String()),
    group_status: Type.Optional(
      Type.KeyOf(
        Type.Object({
          unpaid: Type.String(),
          ongoing_order: Type.String(),
          order_history: Type.String(),
        })
      )
    ),
  }),
]);
export type ReadManyOrderHistory = Static<typeof ReadManyOrderHistorySchema>;

export const ReadManyAuditLogSchema = Type.Intersect([
  ReadManySchema,
  Type.Object({
    topic: Type.Union([
      Type.Literal('order'),
      Type.Literal('user'),
      Type.Literal('product'),
    ]),
    reference_id: Type.String({ format: 'uuid' }),
  }),
]);
export type ReadManyAuditLog = Static<typeof ReadManyAuditLogSchema>;

export const ReadManyUserAdminSchema = Type.Intersect([
  PaginationSchema,
  Type.Object({
    search: Type.Optional(Type.String()),
    role: Type.Optional(
      Type.KeyOf(
        Type.Object({
          super_admin: Type.String(),
          cs_katalog: Type.String(),
          cs_cabang: Type.String(),
          collector: Type.String(),
          buyer_bill_to: Type.String(),
          buyer_ship_to: Type.String(),
        })
      )
    ),
  }),
]);
export type ReadManyUserAdmin = Static<typeof ReadManyUserAdminSchema>;

export const ReadManyUserBuyerSchema = Type.Intersect([
  PaginationSchema,
  Type.Object({
    status: Type.Optional(
      Type.Union([
        Type.KeyOf(
          Type.Object({
            active: Type.String(),
            inactive: Type.String(),
          })
        ),
        Type.Null(),
      ])
    ),
  }),
]);
export type ReadManyUserBuyer = Static<typeof ReadManyUserBuyerSchema>;

export const ProductDivisionFilterSchema = Type.Array(
  Type.Union([
    Type.Object({
      divisi_code: Type.Literal('diamond'),
      name: Type.Literal('Diamond'),
    }),
    Type.Object({
      divisi_code: Type.Literal('trading'),
      name: Type.Literal('Trading'),
    }),
    Type.Object({
      divisi_code: Type.Literal('fnb'),
      name: Type.Literal('Fine Foods & Beverage'),
    }),
    Type.Object({
      divisi_code: Type.Literal('equipment'),
      name: Type.Literal('Equipment'),
    }),
  ])
);
export type ProductDivisionFilter = Static<typeof ProductDivisionFilterSchema>;

export const OrderDivisionFilterSchema = Type.Array(
  Type.Union([
    Type.Object({
      divisi_code: Type.Literal('diamond'),
      name: Type.Literal('Diamond'),
    }),
    Type.Object({
      divisi_code: Type.Literal('trading'),
      name: Type.Literal('Trading'),
    }),
    Type.Object({
      divisi_code: Type.Literal('fnb'),
      name: Type.Literal('Fine Foods & Beverage'),
    }),
    Type.Object({
      divisi_code: Type.Literal('equipment'),
      name: Type.Literal('Equipment'),
    }),
    Type.Object({
      divisi_code: Type.Literal('food'),
      name: Type.Literal('Food'),
    }),
  ])
);
export type OrderDivisionFilter = Static<typeof OrderDivisionFilterSchema>;

export const StorageFilterSchema = Type.Array(
  Type.Union([
    Type.Object({
      storage_code: Type.Literal('dry'),
      name: Type.Literal('Dry'),
    }),
    Type.Object({
      storage_code: Type.Literal('frozen'),
      name: Type.Literal('Frozen'),
    }),
    Type.Object({
      storage_code: Type.Literal('dry_ac'),
      name: Type.Literal('Dry AC'),
    }),
    Type.Object({
      storage_code: Type.Literal('chilled'),
      name: Type.Literal('Chilled'),
    }),
  ])
);
export type StorageFilter = Static<typeof StorageFilterSchema>;

export const SortFilterSchema = Type.Array(
  Type.Union([
    Type.Object({
      sort_by_code: Type.Literal('best_seller_desc'),
      name: Type.Literal('Terlaris'),
    }),
    Type.Object({
      sort_by_code: Type.Literal('brand_asc'),
      name: Type.Literal('Brand A-Z'),
    }),
    Type.Object({
      sort_by_code: Type.Literal('brand_desc'),
      name: Type.Literal('Brand Z-A'),
    }),
    Type.Object({
      sort_by_code: Type.Literal('product_name_asc'),
      name: Type.Literal('Nama Produk A-Z'),
    }),
    Type.Object({
      sort_by_code: Type.Literal('product_name_desc'),
      name: Type.Literal('Nama Produk Z-A'),
    }),
    Type.Object({
      sort_by_code: Type.Literal('price_asc'),
      name: Type.Literal('Harga Terendah'),
    }),
    Type.Object({
      sort_by_code: Type.Literal('price_desc'),
      name: Type.Literal('Harga Tertinggi'),
    }),
  ])
);
export type SortFilter = Static<typeof SortFilterSchema>;

export const ReadManyRecipeSchema = Type.Intersect([
  Type.Object({
    category: Type.Optional(
      Type.KeyOf(
        Type.Object({
          food: Type.String(),
          beverage: Type.String(),
        })
      )
    ),
  }),
]);
export type ReadManyRecipeSchema = Static<typeof ReadManyRecipeSchema>;

export const GroupPromoListBuyerByTypeSchema = Type.Union([
  Type.Literal('general'),
  Type.Literal('specific'),
]);

export const GroupPromoListBuyerSchema = Type.Object({
  type: Type.Optional(GroupPromoListBuyerByTypeSchema),
});

export type GroupPromoListBuyer = Static<typeof GroupPromoListBuyerSchema>;
